import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, Check, ArrowRight, Sparkles, Loader2, Building2, MessageCircle, Mail, FileText, Send, Trophy } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const STEP_ICONS = {
  brand: Building2,
  interview: MessageCircle,
  substack: Mail,
  first_post: FileText,
  publish: Send,
};

export default function LaunchDay() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [working, setWorking] = useState(false);

  const refresh = async () => {
    try { setStatus(await api.launchDayStatus()); } catch (_) { /* noop */ }
  };

  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 6000);
    return () => clearInterval(i);
  }, []);

  if (!status) return <div className="text-sm mono uppercase text-[#A1A1AA]">loading…</div>;

  const pct = Math.round((status.done / status.total) * 100);
  const allDone = status.done === status.total;

  const cta = (step) => {
    switch (step.key) {
      case "brand":
        return { label: "Create brand", action: () => navigate("/brands") };
      case "interview":
        return { label: step.data?.count > 0 ? `Continue interview (${step.data.count}/10)` : "Start interview", action: () => {
          // Link the brand first if needed, then jump to brand detail
          const brandId = status.checklist[0]?.data?.brand_id;
          if (brandId) navigate(`/brands/${brandId}`);
          else navigate("/brands");
        }};
      case "substack":
        return { label: "Connect Substack publication", action: () => {
          const brandId = status.checklist[0]?.data?.brand_id;
          if (brandId) navigate(`/brands/${brandId}`);
          else navigate("/onboarding");
        }};
      case "first_post":
        return { label: "Generate my first post", action: generateFirstPost };
      case "publish":
        return { label: "Open approval queue", action: () => navigate("/approvals") };
      default:
        return null;
    }
  };

  const generateFirstPost = async () => {
    setWorking(true);
    try {
      await api.launchDayGenerateFirstPost();
      toast.success("Generating your first post in the background. Check the approval queue in ~20 seconds.");
      setTimeout(refresh, 6000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start generation");
    } finally { setWorking(false); }
  };

  const finish = async () => {
    setWorking(true);
    try {
      await api.launchDayComplete();
      toast.success("Launch Day complete. You're officially in business.");
      await refresh();
    } finally { setWorking(false); }
  };

  return (
    <div className="max-w-4xl space-y-8" data-testid="launch-day-page">
      <div>
        <div className="text-eyebrow mb-2 flex items-center gap-2"><Rocket size={12}/> Launch Day</div>
        <h1 className="font-display font-black tracking-tighter text-5xl">12 minutes to your first live Substack.</h1>
        <p className="text-sm text-[#52525B] mt-3 max-w-2xl">Five steps. Skip nothing. By the end, you'll have a real brand, real voice, real post, real subscribers receiving it. Then you scale.</p>
      </div>

      {/* PROGRESS BAR */}
      <div className="border-2 border-black p-5" data-testid="progress-bar-block">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-eyebrow">{status.done}/{status.total} steps complete</div>
          <div className="font-display font-black text-2xl tracking-tighter">{pct}%</div>
        </div>
        <div className="h-3 bg-[#F4F5F6] border border-black overflow-hidden">
          <div className="h-full bg-[#F59E0B] transition-all duration-500" style={{ width: `${pct}%` }} data-testid="progress-bar-fill"/>
        </div>
      </div>

      {/* CHECKLIST */}
      <div className="space-y-3">
        {status.checklist.map((step, i) => {
          const Icon = STEP_ICONS[step.key] || Check;
          const isNext = !step.done && step.key === status.next_step;
          const ctaBtn = cta(step);
          return (
            <div
              key={step.key}
              data-testid={`step-${step.key}`}
              className={`border-2 p-5 flex items-center gap-4 transition-colors ${
                step.done ? "border-[#059669] bg-[#ECFDF5]" :
                isNext ? "border-black bg-[#FFFBEB]" :
                "border-[#E2E4E9] bg-white"
              }`}
            >
              <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${
                step.done ? "bg-[#059669] text-white" :
                isNext ? "bg-black text-white" :
                "bg-[#F4F5F6] text-[#A1A1AA]"
              }`}>
                {step.done ? <Check size={20}/> : <Icon size={18}/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-lg">{i + 1}. {step.label}</span>
                  {step.done && <span className="text-xs mono uppercase text-[#059669]">done</span>}
                  {isNext && <span className="text-xs mono uppercase px-2 py-0.5 bg-black text-white">next</span>}
                </div>
                {step.data?.name && <div className="text-xs text-[#52525B] mt-1 truncate">{step.data.name}</div>}
                {step.data?.title && <div className="text-xs text-[#52525B] mt-1 truncate">"{step.data.title}"</div>}
                {step.key === "interview" && step.data?.count > 0 && !step.done && (
                  <div className="text-xs text-[#52525B] mt-1">{step.data.count} of 10 answered</div>
                )}
              </div>
              {!step.done && isNext && ctaBtn && (
                <button
                  onClick={ctaBtn.action}
                  disabled={working}
                  data-testid={`step-${step.key}-cta`}
                  className="bg-black text-white px-4 py-2.5 text-sm whitespace-nowrap flex items-center gap-2 hover:bg-[#002FA7] disabled:opacity-50"
                >
                  {working && step.key === "first_post" ? <Loader2 size={14} className="animate-spin"/> : null}
                  {ctaBtn.label} <ArrowRight size={14}/>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* FINAL CELEBRATION */}
      {allDone && !status.completed_at && (
        <div className="border-4 border-black bg-[#FFFBEB] p-8 text-center brutal-shadow-lg" data-testid="launch-day-celebration">
          <Trophy size={48} className="mx-auto mb-3 text-[#F59E0B]"/>
          <h2 className="font-display font-black tracking-tighter text-4xl mb-2">You did it.</h2>
          <p className="text-sm text-[#52525B] mb-5">Your first Substack post is live. The engine is running. From here, you scale: more brands, more posts, more subscribers, autopilot.</p>
          <button onClick={finish} disabled={working} className="bg-black text-white px-6 py-3 text-sm hover:bg-[#F59E0B] hover:text-black inline-flex items-center gap-2" data-testid="finish-launch-button">
            <Sparkles size={14}/> Finish Launch Day
          </button>
        </div>
      )}

      {/* TIPS */}
      <div className="border border-[#E2E4E9] p-5 text-xs text-[#52525B] space-y-2" data-testid="launch-day-tips">
        <div className="text-eyebrow text-[#0A0A0B] mb-2">As you go</div>
        <p>· If you don't have a Substack yet, jump to <a href="/onboarding" className="underline">Substack Setup</a> — we walk you through creating the publication, then a 60-second magic-link sign-in to connect it.</p>
        <p>· Stuck on the interview? Just answer honestly in your normal voice. We use this to teach the AI HOW you talk, not just WHAT you say.</p>
        <p>· You can come back any time. Progress saves automatically.</p>
      </div>
    </div>
  );
}
